import { ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Section 6 of the Fraud Hub: a static set of "explore further" links
 * to the Superset dashboards that deep-dive each angle.
 *
 * No hook, no API call, no skeleton — just a hand-curated list of the
 * five most-useful Superset URLs. Caddy on the VPS routes `/superset/*`
 * to the Superset container, so these are relative URLs.
 *
 * Visual weight is intentionally low (subtle border, smaller card title)
 * so this section sits as the "footer" of the narrative — the page's
 * primary story is told above it.
 */

interface SupersetLink {
  label: string;
  desc: string;
  path: string;
}

const SUPERSET_LINKS: SupersetLink[] = [
  {
    label: 'Rule Activity Overview',
    desc: 'Per-rule volume, severity mix, recent trend',
    path: '/superset/superset/dashboard/fb-rule-activity/',
  },
  {
    label: 'Rule Co-Fire Matrix',
    desc: 'Which rules fire together on the same vehicle',
    path: '/superset/superset/dashboard/fb-cofire/',
  },
  {
    label: 'Per-Station Activity',
    desc: 'Top stations / merchants by alert volume',
    path: '/superset/superset/dashboard/fb-stations/',
  },
  {
    label: 'Geographic Heatmap',
    desc: 'Alert density across SA',
    path: '/superset/superset/dashboard/fb-geo/',
  },
  {
    label: 'Per-Vehicle Investigator',
    desc: 'Drill into a single asset across all rules',
    path: '/superset/superset/dashboard/fb-vehicle/',
  },
];

export function DrillDeeperLinks(): JSX.Element {
  return (
    <Card className="border-muted-foreground/15 bg-muted/10">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Explore further →
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {SUPERSET_LINKS.map((link) => (
            <a
              key={link.path}
              href={link.path}
              target="_blank"
              rel="noreferrer"
              className="group flex flex-col gap-1 rounded-md border bg-background p-3 transition-colors hover:bg-accent/40"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-semibold leading-tight">
                  {link.label}
                </span>
                <ExternalLink
                  className="h-3 w-3 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
                  aria-hidden
                />
              </div>
              <span className="text-xs leading-snug text-muted-foreground">
                {link.desc}
              </span>
            </a>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default DrillDeeperLinks;
